import { defineStore } from "pinia";
import { ref, computed, onUnmounted } from "vue";
import { disconnectGlobalSocket } from "@/composables/useSocketIO";

/**
 * 用户信息缓存键名
 */
const USER_INFO_KEY = "user_info";
/**
 * Token缓存键名
 */
const TOKEN_KEY = "token";
/**
 * 缓存过期时间（1天）
 */
const CACHE_EXPIRE_TIME = 1 * 24 * 60 * 60 * 1000;

/**
 * 缓存用户信息接口
 */
interface CachedUserInfo {
  /** 用户信息 */
  data: UserInfo;
  /** 缓存时间戳 */
  timestamp: number;
}

/**
 * 用户存储管理
 * @description 管理用户认证状态和用户信息，支持长期缓存策略
 */
export const useUserStore = defineStore("user", () => {
  const token = ref<string>("");
  const userInfo = ref<UserInfo | null>(null);

  const isLogin = computed(() => !!token.value);

  /**
   * 处理storage事件，实现跨标签页同步
   */
  const handleStorageChange = (event: StorageEvent) => {
    // 只处理当前应用的localStorage变化
    if (event.storageArea !== localStorage) return;
    
    console.log("[用户Store] storage事件:", event.key, event.newValue, event.oldValue);
    
    if (event.key === TOKEN_KEY) {
      const newToken = event.newValue;
      if (newToken !== token.value) {
        token.value = newToken || "";
        console.log("[用户Store] token同步更新:", newToken ? "有token" : "无token");
      }
    } else if (event.key === USER_INFO_KEY) {
      if (event.newValue === null) {
        // 用户信息被清除
        userInfo.value = null;
        console.log("[用户Store] 用户信息同步清除");
      } else if (event.newValue) {
        try {
          const cachedInfo: CachedUserInfo = JSON.parse(event.newValue);
          const now = Date.now();
          if (now - cachedInfo.timestamp < CACHE_EXPIRE_TIME) {
            userInfo.value = { ...cachedInfo.data, lastUpdateTime: cachedInfo.timestamp };
            console.log("[用户Store] 用户信息同步更新，用户ID:", cachedInfo.data.id);
          } else {
            // 缓存过期，清除本地存储
            localStorage.removeItem(USER_INFO_KEY);
            userInfo.value = null;
            console.log("[用户Store] 用户信息缓存已过期，已清除");
          }
        } catch (error) {
          console.error("[用户Store] 解析同步的用户信息失败:", error);
        }
      }
    }
  };
  
  /**
   * 初始化跨标签页同步
   */
  const initCrossTabSync = () => {
    // 添加storage事件监听
    window.addEventListener("storage", handleStorageChange);
    console.log("[用户Store] 跨标签页同步已启用");
  };
  
  /**
   * 清理跨标签页同步
   */
  const cleanupCrossTabSync = () => {
    window.removeEventListener("storage", handleStorageChange);
    console.log("[用户Store] 跨标签页同步已清理");
  };

  /**
   * 初始化用户状态
   */
  function initUserState() {
    // 从本地存储加载token
    const savedToken = localStorage.getItem(TOKEN_KEY);
    if (savedToken) {
      token.value = savedToken;
    }

    // 从本地存储加载用户信息
    const savedUserInfo = localStorage.getItem(USER_INFO_KEY);
    if (savedUserInfo) {
      try {
        const cachedInfo: CachedUserInfo = JSON.parse(savedUserInfo);

        // 检查缓存是否过期
        const now = Date.now();
        if (now - cachedInfo.timestamp < CACHE_EXPIRE_TIME) {
          userInfo.value = { ...cachedInfo.data, lastUpdateTime: cachedInfo.timestamp };
        } else {
          // 缓存过期，清除本地存储
          localStorage.removeItem(USER_INFO_KEY);
        }
      } catch (error) {
        console.error("解析用户信息缓存失败:", error);
        localStorage.removeItem(USER_INFO_KEY);
      }
    }
  }

  /**
   * 设置Token
   * @param newToken 新的token
   */
  function setToken(newToken: string) {
    token.value = newToken;
    localStorage.setItem(TOKEN_KEY, newToken);
  }

  /**
   * 设置用户信息
   * @param info 用户信息
   */
  function setUserInfo(info: UserInfo) {
    const userInfoWithTime = {
      ...info,
      lastUpdateTime: Date.now(),
    };

    userInfo.value = userInfoWithTime;

    // 缓存用户信息到本地存储
    const cachedInfo: CachedUserInfo = {
      data: userInfoWithTime,
      timestamp: Date.now(),
    };
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(cachedInfo));
  }

  /**
   * 更新用户信息
   * @param updates 更新的字段
   */
  function updateUserInfo(updates: Partial<UserInfo>) {
    if (userInfo.value) {
      const updatedInfo = {
        ...userInfo.value,
        ...updates,
        lastUpdateTime: Date.now(),
      };
      setUserInfo(updatedInfo);
    }
  }

  /**
   * 刷新用户信息
   */
  function refreshUserInfo() {
    // 这里可以调用API获取最新的用户信息
    // 暂时只更新缓存时间
    if (userInfo.value) {
      setUserInfo(userInfo.value);
    }
  }

  /**
   * 退出登录
   */
  function logout() {
    // 断开Socket连接
    disconnectGlobalSocket();

    token.value = "";
    userInfo.value = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
  }

  /**
   * 清除过期缓存
   */
  function clearExpiredCache() {
    const savedUserInfo = localStorage.getItem(USER_INFO_KEY);
    if (savedUserInfo) {
      try {
        const cachedInfo: CachedUserInfo = JSON.parse(savedUserInfo);
        const now = Date.now();
        if (now - cachedInfo.timestamp >= CACHE_EXPIRE_TIME) {
          localStorage.removeItem(USER_INFO_KEY);
        }
      } catch (error) {
        console.error("解析用户信息缓存失败:", error);
        localStorage.removeItem(USER_INFO_KEY);
      }
    }
  }

  // 初始化用户状态
  initUserState();
  
  // 初始化跨标签页同步
  initCrossTabSync();
  
  // 在组件卸载时清理（虽然store通常不会卸载，但为了安全）
  onUnmounted(() => {
    cleanupCrossTabSync();
  });

  return {
    token,
    userInfo,
    isLogin,
    setToken,
    setUserInfo,
    updateUserInfo,
    refreshUserInfo,
    logout,
    clearExpiredCache,
    // 导出清理函数以供需要时使用
    cleanupCrossTabSync,
  };
});
